import { useListIntegrationDefinitions, useListIntegrations } from "@/queries/integration-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useMemo } from "react";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Integration, IntegrationDefinition } from "gitpod-next-api/gitpod/v1/integration_pb";

export enum IntegrationRequirementStatus {
    Ready = "ready",
    Disabled = "disabled",
    NotCreated = "not_created",
}

export type IntegrationRequirement = {
    name: string;
    status: IntegrationRequirementStatus;
    definition: PlainMessage<IntegrationDefinition> | undefined;
};

export type IntegrationRequirementsResult = {
    requirements: IntegrationRequirement[];
    allReady: boolean;
};

export const resolveIntegrationRequirements = (
    requiredIntegrations: readonly string[],
    definitions: PlainMessage<IntegrationDefinition>[] | undefined,
    integrations: PlainMessage<Integration>[] | undefined,
): IntegrationRequirementsResult => {
    if (requiredIntegrations.length === 0) {
        return { requirements: [], allReady: true };
    }

    if (!definitions) {
        return { requirements: [], allReady: false };
    }

    const definitionsByName = new Map(definitions.map((d) => [d.name, d]));

    const requirements: IntegrationRequirement[] = requiredIntegrations.map((name) => {
        const definition = definitionsByName.get(name);
        if (!definition) {
            return { name, status: IntegrationRequirementStatus.NotCreated, definition: undefined };
        }

        const integration = integrations?.find((i) => i.integrationDefinitionId === definition.id);
        if (!integration) {
            return { name, status: IntegrationRequirementStatus.NotCreated, definition };
        }

        return {
            name,
            status: integration.enabled ? IntegrationRequirementStatus.Ready : IntegrationRequirementStatus.Disabled,
            definition,
        };
    });

    const allReady = requirements.every((r) => r.status === IntegrationRequirementStatus.Ready);

    return { requirements, allReady };
};

export const useIntegrationRequirements = (
    requiredIntegrations: readonly string[],
): IntegrationRequirementsResult & { isLoading: boolean } => {
    const { data: user } = useAuthenticatedUser();
    const { data: definitions, isLoading: isLoadingDefinitions } = useListIntegrationDefinitions();
    const { data: integrations, isLoading: isLoadingIntegrations } = useListIntegrations(user?.organizationId);

    const isLoading = isLoadingDefinitions || isLoadingIntegrations;

    return useMemo(() => {
        if (isLoading) {
            return { requirements: [], allReady: false, isLoading };
        }

        return { ...resolveIntegrationRequirements(requiredIntegrations, definitions, integrations), isLoading };
    }, [requiredIntegrations, definitions, integrations, isLoading]);
};
